import { reactive, shallowRef } from 'vue';
import { applyBranding, useBranding } from '@/branding';
import { apiRequest, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';

// Editable copy of the school settings. The page creates it once and
// provides it to the sections (see SCHOOL_SETTINGS_KEY). The API takes the
// full record: a section saves its own fields plus the *saved* values of
// everything else, so unsaved edits in another section are never sent.
export const SCHOOL_SETTINGS_KEY = Symbol('school-settings');
const FIELDS = [
  'name',
  'shortName',
  'tagline',
  'brandColor',
  'depedSchoolId',
  'address',
  'district',
  'division',
  'region',
  'emailSenderName',
  'loginHeadline',
  'loginOverlay',
  'loginShowFeatures',
  'contactPhone',
  'contactEmail',
  'form29CheckedByJhsID',
  'form29CheckedByShsID',
  'form29RecommendingID',
  'form29ApprovedByID',
  'form29PreparedByJhsName',
  'form29PreparedByJhsPosition',
  'form29PreparedByShsName',
  'form29PreparedByShsPosition',
  'form29CheckedByJhsName',
  'form29CheckedByJhsPosition',
  'form29CheckedByShsName',
  'form29CheckedByShsPosition',
  'form29ReviewedBy1Name',
  'form29ReviewedBy1Position',
  'form29ReviewedBy2Name',
  'form29ReviewedBy2Position',
  'form29RecommendingName',
  'form29RecommendingPosition',
  'form29ApprovedByName',
  'form29ApprovedByPosition',
];
// Picked from a list; empty is null, not ''.
const IDS = new Set([
  'form29CheckedByJhsID',
  'form29CheckedByShsID',
  'form29RecommendingID',
  'form29ApprovedByID',
]);
const blank = (k) => (IDS.has(k) ? null : '');

export function useSchoolSettingsForm() {
  const branding = useBranding();
  const toast = useToast();

  const snapshot = () => Object.fromEntries(FIELDS.map((k) => [k, branding[k] ?? blank(k)]));
  const form = reactive(snapshot());
  const saving = shallowRef('');
  const savedAt = shallowRef({}); // section -> timestamp, for "Saved."

  const isDirty = (keys) => keys.some((k) => (form[k] ?? blank(k)) !== (branding[k] ?? blank(k)));

  function reset(keys) {
    for (const k of keys) form[k] = branding[k] ?? blank(k);
  }

  async function save(section, keys) {
    saving.value = section;
    try {
      const body = Object.fromEntries(
        FIELDS.map((k) => [k, (keys.includes(k) ? form[k] : branding[k]) ?? blank(k)]),
      );
      applyBranding(await apiRequest('put', '/school-settings', body));
      savedAt.value = { ...savedAt.value, [section]: Date.now() };
      return true;
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save the settings.'));
      return false;
    } finally {
      saving.value = '';
    }
  }

  return { form, saving, savedAt, isDirty, reset, save };
}
